/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { ICartNumberOfItemsContext, IChatContext, TContextProvider } from '../../../interfaces';
import { ProactiveChatNames } from '../../helpers';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * CartNumberOfItemsTrigger class.
 * Proactive chat is triggered if Cart Number of Items is in preset boundaries (config.proactiveOnCartNoOfItemsMin, config.proactiveOnCartNoOfItemsMax).
 */
export default class CartNumberOfItemsTrigger extends ProactiveChatTrigger {
    private min?: number;
    private max?: number;
    private numberOfItems: number = 0;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.min = this.props.config.proactiveOnCartNoOfItemsMin;
        this.max = this.props.config.proactiveOnCartNoOfItemsMax;

        this.props.context.telemetry.debug(
            `[${CartNumberOfItemsTrigger.name}] Cart number of items proactive chat initialized with min: '${this.min}'; max: '${this.max}'.`
        );
    }

    /**
     * Validates whether the conditions are met to trigger proactive chat: the number of items in the cart should be within the min and max values.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        const cart = await this._getCart();
        if (cart.isEmpty) {
            this.props.context.telemetry.debug(`[Chat][${CartNumberOfItemsTrigger.name}] Cart was empty. Validation returning 'false'.`);
            return false;
        }

        this.numberOfItems = cart.cart.TotalItems || 0;

        let retVal: boolean = false;
        if (this.min) {
            if (this.max) {
                retVal = this.min <= this.numberOfItems && this.numberOfItems <= this.max;
            } else {
                retVal = this.min <= this.numberOfItems;
            }
        } else if (this.max) {
            retVal = this.numberOfItems <= this.max;
        }

        this.props.context.telemetry.debug(
            `[${CartNumberOfItemsTrigger.name}] Validation result: '${retVal}'; Number of items in cart: '${this.numberOfItems}'.`
        );
        return retVal;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): ICartNumberOfItemsContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.NumberOfItems,
                    isDisplayable: true
                },
                CartNumberOfItems: {
                    value: this.numberOfItems,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnCartNoOfItemsMessage || this.props.config.proactiveDefaultMessage;
    }

    /**
     * Gets the cart.
     * @returns {Promise<ICartState>} The cart.
     */
    private async _getCart(): Promise<ICartState> {
        return getCartState(this.props.context.actionContext);
    }
}
